
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertTriangle, 
  Shield, 
  Search, 
  Filter,
  ArrowLeft,
  Download,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface SecurityLog {
  id: string;
  user_id: string;
  username?: string;
  incident_type: string;
  description: string;
  ip_address?: string;
  resolved: boolean;
  created_at: string;
  resolved_at?: string;
  resolved_by?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface SecurityLogsProps {
  onBack: () => void;
}

const SecurityLogs: React.FC<SecurityLogsProps> = ({ onBack }) => {
  const [logs, setLogs] = useState<SecurityLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<SecurityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');

  useEffect(() => {
    loadSecurityLogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, searchQuery, statusFilter, severityFilter]);

  const loadSecurityLogs = async () => {
    try {
      setLoading(true);
      
      // For now, we'll use mock data since we don't have a security_incidents table yet
      // In a real implementation, you'd query your security logs table
      const mockLogs: SecurityLog[] = [
        {
          id: '1',
          user_id: 'user-1',
          username: 'john_doe',
          incident_type: 'Failed Login Attempt',
          description: 'Multiple failed login attempts (5 attempts) from same IP',
          ip_address: '192.168.1.100',
          resolved: true,
          created_at: new Date(Date.now() - 86400000).toISOString(),
          resolved_at: new Date(Date.now() - 3600000).toISOString(),
          resolved_by: 'Medusa',
          severity: 'medium'
        },
        {
          id: '2',
          user_id: 'user-2',
          username: 'jane_smith',
          incident_type: 'Suspicious Activity',
          description: 'Unusual access pattern detected - login from different country',
          ip_address: '10.0.0.50',
          resolved: false,
          created_at: new Date(Date.now() - 172800000).toISOString(),
          severity: 'high'
        },
        {
          id: '3',
          user_id: 'user-3',
          username: 'coach_mike',
          incident_type: 'Account Lockout',
          description: 'Account temporarily locked due to suspicious activity',
          ip_address: '203.0.113.1',
          resolved: true,
          created_at: new Date(Date.now() - 259200000).toISOString(),
          resolved_at: new Date(Date.now() - 86400000).toISOString(),
          resolved_by: 'Medusa',
          severity: 'high'
        },
        {
          id: '4',
          user_id: 'user-4',
          username: 'player_alex',
          incident_type: 'Password Breach Attempt',
          description: 'Attempted password reset from unverified email',
          ip_address: '198.51.100.42',
          resolved: false,
          created_at: new Date(Date.now() - 345600000).toISOString(),
          severity: 'critical'
        },
        {
          id: '5',
          user_id: 'user-5',
          username: 'venue_manager',
          incident_type: 'Data Access Violation',
          description: 'Attempted access to restricted data without permission',
          ip_address: '172.16.0.1',
          resolved: true,
          created_at: new Date(Date.now() - 432000000).toISOString(),
          resolved_at: new Date(Date.now() - 172800000).toISOString(),
          resolved_by: 'Medusa',
          severity: 'critical'
        }
      ];
      
      setLogs(mockLogs);
    } catch (error) {
      console.error('Error loading security logs:', error);
      toast.error('Failed to load security logs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(log => 
        log.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.incident_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.ip_address?.includes(searchQuery)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(log => 
        statusFilter === 'resolved' ? log.resolved : !log.resolved
      );
    }

    // Severity filter
    if (severityFilter !== 'all') {
      filtered = filtered.filter(log => log.severity === severityFilter);
    }

    setFilteredLogs(filtered);
  };

  const handleResolveIncident = async (logId: string) => {
    try {
      // In a real implementation, you'd update the database
      setLogs(prev => prev.map(log => 
        log.id === logId 
          ? { 
              ...log, 
              resolved: true, 
              resolved_at: new Date().toISOString(),
              resolved_by: 'Medusa'
            }
          : log
      ));
      toast.success('Security incident marked as resolved');
    } catch (error) {
      console.error('Error resolving incident:', error);
      toast.error('Failed to resolve incident');
    }
  };

  const handleExportLogs = () => {
    try {
      const csvContent = [
        ['ID', 'Username', 'Incident Type', 'Description', 'IP Address', 'Severity', 'Status', 'Created At', 'Resolved At', 'Resolved By'],
        ...filteredLogs.map(log => [
          log.id,
          log.username || '',
          log.incident_type,
          log.description,
          log.ip_address || '',
          log.severity,
          log.resolved ? 'Resolved' : 'Pending',
          new Date(log.created_at).toLocaleString(),
          log.resolved_at ? new Date(log.resolved_at).toLocaleString() : '',
          log.resolved_by || ''
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security_logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Security logs exported successfully');
    } catch (error) {
      console.error('Error exporting logs:', error);
      toast.error('Failed to export logs');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-blue-500';
      case 'medium': return 'bg-yellow-500';
      case 'high': return 'bg-orange-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getIncidentTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'failed login attempt': return 'bg-yellow-500';
      case 'suspicious activity': return 'bg-orange-500';
      case 'account lockout': return 'bg-red-500';
      case 'password breach attempt': return 'bg-red-600';
      case 'data access violation': return 'bg-red-700';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading security logs...</div>
      </div>
    );
  }

  const unresolvedCount = filteredLogs.filter(log => !log.resolved).length;
  const criticalCount = filteredLogs.filter(log => log.severity === 'critical').length;
  const highCount = filteredLogs.filter(log => log.severity === 'high').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          onClick={onBack}
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Panel
        </Button>
        <h1 className="text-2xl font-bold text-white">Security Logs</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Incidents</p>
                <p className="text-2xl font-bold text-white">{filteredLogs.length}</p>
              </div>
              <Shield className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Unresolved</p>
                <p className="text-2xl font-bold text-red-400">{unresolvedCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Critical</p>
                <p className="text-2xl font-bold text-red-500">{criticalCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">High Priority</p>
                <p className="text-2xl font-bold text-orange-400">{highCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search logs by username, incident type, or IP address..."
                  className="bg-gray-700 border-gray-600 text-white pl-10"
                />
              </div>
            </div>
            
            <div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="all">All Severity</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={loadSecurityLogs}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              
              <Button
                onClick={handleExportLogs}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Logs Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Security Incidents ({filteredLogs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700">
                  <TableHead className="text-gray-300">User</TableHead>
                  <TableHead className="text-gray-300">Incident</TableHead>
                  <TableHead className="text-gray-300">Description</TableHead>
                  <TableHead className="text-gray-300">IP Address</TableHead>
                  <TableHead className="text-gray-300">Severity</TableHead>
                  <TableHead className="text-gray-300">Status</TableHead>
                  <TableHead className="text-gray-300">Date</TableHead>
                  <TableHead className="text-gray-300">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow key={log.id} className="border-gray-700">
                    <TableCell className="text-white font-medium">
                      {log.username || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getIncidentTypeColor(log.incident_type)} text-white`}>
                        {log.incident_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-300 max-w-xs truncate">
                      {log.description}
                    </TableCell>
                    <TableCell className="text-gray-300 font-mono text-sm">
                      {log.ip_address || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getSeverityColor(log.severity)} text-white capitalize`}>
                        {log.severity}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={log.resolved ? 'bg-green-500' : 'bg-red-500'}>
                        {log.resolved ? 'Resolved' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-300">
                      {new Date(log.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {!log.resolved && (
                        <Button
                          onClick={() => handleResolveIncident(log.id)}
                          size="sm"
                          className="bg-sports-green text-black hover:bg-green-600"
                        >
                          Resolve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityLogs;
